import React, { useState, useRef, useEffect, useCallback } from 'react';
import './HeaderBar.css';
import { Layout, Input, Avatar, Button, Dropdown, Menu, Typography } from 'antd';
import {
    UserOutlined, LogoutOutlined, SettingOutlined, SearchOutlined,
    LeftOutlined, RightOutlined, MenuOutlined, DatabaseOutlined,
    RobotOutlined, FileOutlined, AppstoreOutlined,
} from '@ant-design/icons';
import { useKeycloak } from '@react-keycloak/web';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import NotificationCenter from './NotificationCenter';
import { useSearch } from '../contexts/SearchContext';

const { Header } = Layout;
const { Text, Title } = Typography;

const SECTION_ICONS = {
    collection: <DatabaseOutlined />,
    assistant: <RobotOutlined />,
    file: <FileOutlined />,
    default: <FileOutlined />,
};

const GLOBAL_PAGES = [
    { path: '/collections', label: 'Collections', icon: <DatabaseOutlined /> },
    { path: '/assistants', label: 'Assistants', icon: <RobotOutlined /> },
    { path: '/dashboard/overview', label: 'Dashboard', icon: <AppstoreOutlined /> },
];

const HeaderBar = ({ collapsed, onToggle, onMobileMenuClick, isMobile }) => {
    const { keycloak } = useKeycloak();
    const navigate = useNavigate();
    const location = useLocation();
    const { sectionLabel, searchInSection, setQuery } = useSearch();

    const [searchValue, setSearchValue] = useState('');
    const [focused, setFocused] = useState(false);
    const [sectionResults, setSectionResults] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    const openPath = (path) => {
        if (!keycloak.authenticated) {
            keycloak.login({ redirectUri: window.location.href });
            return;
        }
        navigate(path);
    };
    const handleLogout = () => keycloak.logout();
    const handleProfile = () => {
        const userId = keycloak.tokenParsed?.sub;
        if (userId) openPath(`/users/${userId}/profile`);
    };

    const globalResults = searchValue.trim() ? GLOBAL_PAGES.filter(p =>
        p.label.toLowerCase().includes(searchValue.toLowerCase()) && p.path !== location.pathname
    ) : [];

    const allResults = [...sectionResults, ...globalResults.map(p => ({ id: `nav-${p.path}`, title: `Go to ${p.label}`, type: 'nav', icon: p.icon, action: () => openPath(p.path) }))];

    const handleSearch = useCallback((value) => {
        setSearchValue(value);
        setQuery(value);
        setSelectedIndex(-1);
        if (value.trim()) {
            setSectionResults(searchInSection(value) || []);
        } else {
            setSectionResults([]);
            searchInSection('');
        }
    }, [searchInSection, setQuery]);

    const handleSelect = useCallback((item) => {
        if (item.action) item.action();
        setSearchValue('');
        setQuery('');
        setSectionResults([]);
        searchInSection('');
        setFocused(false);
        inputRef.current?.blur();
    }, [setQuery, searchInSection]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(i => Math.min(i + 1, allResults.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(i => Math.max(i - 1, -1)); }
        else if (e.key === 'Enter' && selectedIndex >= 0 && allResults[selectedIndex]) { e.preventDefault(); handleSelect(allResults[selectedIndex]); }
        else if (e.key === 'Escape') { setFocused(false); inputRef.current?.blur(); }
    }, [allResults, selectedIndex, handleSelect]);

    useEffect(() => {
        const handleClickOutside = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setFocused(false); };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const showDropdown = focused && searchValue.trim().length > 0 && allResults.length > 0;

    const menu = (
        <Menu>
            <Menu.Item key="profile" icon={<UserOutlined />} onClick={handleProfile}>Profile</Menu.Item>
            <Menu.Item key="settings" icon={<SettingOutlined />}>Settings</Menu.Item>
            <Menu.Divider />
            <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>Logout</Menu.Item>
        </Menu>
    );

    return (
        <Header className="app-header">
            <div className="header-left">
                {isMobile ? (
                    <button className="header-toggle-btn" onClick={onMobileMenuClick} aria-label="Open menu"><MenuOutlined /></button>
                ) : (
                    <button className="header-toggle-btn" onClick={onToggle} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
                        {collapsed ? <RightOutlined /> : <LeftOutlined />}
                    </button>
                )}
                <Link
                    to="/dashboard/overview"
                    className="header-logo"
                    onClick={(event) => {
                        if (keycloak.authenticated) return;
                        event.preventDefault();
                        keycloak.login({ redirectUri: window.location.href });
                    }}
                >
                    <img src="/logo.svg" alt="FocusML" className="header-logo-icon" />
                    <Title level={4} className="header-logo-text">FocusML</Title>
                </Link>
            </div>

            <div className="header-center" ref={dropdownRef}>
                <Input
                    ref={inputRef}
                    placeholder={sectionLabel ? `Search in ${sectionLabel}...` : 'Search...'}
                    prefix={<SearchOutlined style={{ color: 'var(--color-text-muted)' }} />}
                    className="header-search"
                    value={searchValue}
                    onChange={e => handleSearch(e.target.value)}
                    onFocus={() => setFocused(true)}
                    onKeyDown={handleKeyDown}
                    allowClear
                />
                {showDropdown && (
                    <div className="header-search-dropdown">
                        {sectionLabel && sectionResults.length > 0 && (
                            <div className="search-section-label">In {sectionLabel}</div>
                        )}
                        {sectionResults.map((item, idx) => (
                            <div key={item.id} className={`search-result-item ${idx === selectedIndex ? 'search-result-active' : ''}`} onClick={() => handleSelect(item)} onMouseEnter={() => setSelectedIndex(idx)}>
                                <span className="search-result-icon">{SECTION_ICONS[item.type] || SECTION_ICONS.default}</span>
                                <div className="search-result-text">
                                    <div className="search-result-title">{item.title}</div>
                                    {item.subtitle && <div className="search-result-subtitle">{item.subtitle}</div>}
                                </div>
                            </div>
                        ))}
                        {globalResults.length > 0 && (
                            <>
                                {sectionResults.length > 0 && <div className="search-divider" />}
                                <div className="search-section-label">Go to</div>
                                {globalResults.map((p, idx) => {
                                    const absIdx = sectionResults.length + idx;
                                    return (
                                        <div key={p.path} className={`search-result-item ${absIdx === selectedIndex ? 'search-result-active' : ''}`} onClick={() => { openPath(p.path); setSearchValue(''); setQuery(''); setSectionResults([]); searchInSection(''); setFocused(false); }} onMouseEnter={() => setSelectedIndex(absIdx)}>
                                            <span className="search-result-icon">{p.icon}</span>
                                            <div className="search-result-text">
                                                <div className="search-result-title">{p.label}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className="header-right">
                {keycloak.authenticated ? (
                    <>
                        <NotificationCenter />
                        <Dropdown overlay={menu} placement="bottomRight" trigger={['click']}>
                            <div className="header-user">
                                <div className="header-user-info">
                                    <Text className="header-user-name">{keycloak.tokenParsed?.name || 'User'}</Text>
                                    <Text className="header-user-email">{keycloak.tokenParsed?.email || ''}</Text>
                                </div>
                                <Avatar size={36} icon={<UserOutlined />} className="header-avatar" />
                            </div>
                        </Dropdown>
                    </>
                ) : (
        <button
          type="button"
          className="header-sign-in"
          onClick={() => keycloak.login({ redirectUri: window.location.href })}
        >
          Sign in
        </button>
                )}
            </div>
        </Header>
    );
};

export default HeaderBar;
